import React, { useState, useEffect } from 'react';
import {
  MdDashboard,
  MdPeople,
  MdCalendarToday,
  MdLibraryBooks,
  MdSettings,
  MdFeedback,
} from 'react-icons/md';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const AdminDash = () => {
  const [activeTab, setActiveTab] = useState('Home');
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [adminName, setAdminName] = useState('Admin');

  const tabs = [
    { label: 'Home', icon: <MdDashboard /> },
    { label: 'Users', icon: <MdPeople /> },
    { label: 'Sessions', icon: <MdCalendarToday /> },
    { label: 'Resources', icon: <MdLibraryBooks /> },
    { label: 'Feedback', icon: <MdFeedback /> },
    { label: 'Settings', icon: <MdSettings /> },
  ];

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (admin) => {
      if (admin) {
        setAdminName(admin.displayName || 'Admin');
        const userSnapshot = await getDocs(collection(db, 'users'));
        setUsers(userSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        const sessionSnapshot = await getDocs(collection(db, 'sessions'));
        setSessions(sessionSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }
    });
    return () => unsubscribe();
  }, []);

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Delete this user?')) {
      await deleteDoc(doc(db, 'users', userId));
      setUsers(prev => prev.filter(u => u.id !== userId));
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'Users':
        return (
          <div>
            <h2 className="text-xl font-semibold mb-4">👥 All Users</h2>
            <ul className="space-y-3">
              {users.map(user => (
                <li key={user.id} className="border p-4 rounded-md flex justify-between items-center">
                  <div>
                    <p className="font-medium">{user.fullName || user.email}</p>
                    <p className="text-sm text-gray-600">{user.email}</p>
                  </div>
                  <button onClick={() => handleDeleteUser(user.id)} className="text-red-500 text-sm underline">Delete</button>
                </li>
              ))}
            </ul>
          </div>
        );
      case 'Sessions':
        return (
          <div>
            <h2 className="text-xl font-semibold mb-4">📅 All Sessions</h2>
            <ul className="space-y-3">
              {sessions.map(session => (
                <li key={session.id} className="border p-4 rounded-md">
                  <p><strong>{session.type}</strong> with {session.clientName || session.userId}</p>
                  <p className="text-sm text-gray-600">{session.date} at {session.time}</p>
                </li>
              ))}
            </ul>
          </div>
        );
      case 'Resources':
        return (
          <div>
            <h2 className="text-xl font-semibold mb-4">📚 Manage Resources</h2>
            <p className="text-sm text-gray-600">Feature coming soon: upload and organize resources.</p>
          </div>
        );
      case 'Feedback':
        return <p className="text-gray-700">Feature coming soon: read and respond to user feedback.</p>;
      case 'Settings':
        return <p className="text-gray-700">Feature coming soon: system-wide admin settings.</p>;
      case 'Home':
      default:
        return (
          <div>
            <h1 className="text-2xl font-bold mb-4">Welcome back, {adminName}!</h1>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <DashboardCard icon="👥" label={`${users.length} Users`} />
              <DashboardCard icon="📅" label={`${sessions.length} Sessions`} />
              <DashboardCard icon="📚" label="Resources" />
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <div className="flex flex-1 py-14">
        <aside className="w-64 border-r border-gray-200 p-6 hidden md:block">
          <ul className="space-y-4">
            {tabs.map(({ label, icon }) => (
              <li key={label}>
                <button
                  onClick={() => setActiveTab(label)}
                  className={`flex items-center gap-3 px-2 py-2 text-md rounded w-full text-left ${activeTab === label ? 'bg-[#dbe5dc]' : 'hover:bg-[#f1f4ec]'}`}
                >
                  <span className="text-xl">{icon}</span>
                  {label}
                </button>
              </li>
            ))}
          </ul>
        </aside>
        <main className="flex-1 p-6 md:p-12 bg-white">
          {renderTabContent()}
        </main>
      </div>
      <Footer />
    </div>
  );
};

function DashboardCard({ icon, label }) {
  return (
    <div className="flex flex-col items-center justify-center border border-gray-200 rounded-md p-4 bg-white hover:shadow-sm transition">
      <div className="text-3xl mb-2">{icon}</div>
      <p className="text-sm font-medium text-gray-800 text-center">{label}</p>
    </div>
  );
}

export default AdminDash;
